import { UseQueryResult } from '@tanstack/react-query'
import {
  getAccountLinks,
  getEmcConfig,
  getStorageConfig,
  getDeviceLinks,
  getEmcIdlePeriods,
  getInstallationConfig,
  getInstallation,
  getInstallations,
  getInstallationsCount,
  getPowerBuyPrices,
  getPowerSellPrices,
  getStorageBatteryOverview,
  getSubDevices,
  SubDevicesDto,
  EmcConfigDto,
  StorageConfigDto,
  AccountLinkDto,
  InstallationDto,
  EmcIdlePeriodDto,
  InstallationPowerPriceDto,
  InstallationConfigDto,
  BatteryOverviewDto,
  getDynamicTariff,
} from '../api'
import { transposeArrayType } from '../../utils/transpose-array-type'
import { Installation } from 'src/Libs/installations/models/installation.model'
import { isUndefined } from 'lodash'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { getModbusTcpServer } from '../../advanced-settings/overview/api/modbus-tcp-server'
import { ModbusTCPServerDto } from '../../advanced-settings/overview/types/modbus-tcp-server.types'
import { WallboxIdentityStatusDto, getWallboxesIdentityStatus } from '../../advanced-settings/charging-stations'
import { InstallationDevices } from '../../types/installation'
import { usePortalQueries, usePortalQuery, usePortalQuery as usePortalQueryFromPortal } from 'src/Libs/api'
import { InstallationStatus } from 'src/Libs/installation-status'
import { DynamicTariffDto } from '../types'

export const userInstallationsKey = (userId: number | null) => ['user', userId, 'installations'] as const
export const useUserInstallations = (userId: number | null): UseQueryResult<Installation[], Error> => {
  const authFetch = useAuthFetchWithResponse()

  return usePortalQueryFromPortal({
    queryKey: userInstallationsKey(userId),
    queryFn: () =>
      getInstallations(authFetch, 2)
        .then(res => res.value)
        .then(data => (data ? transposeArrayType(data) : [])),
    enabled: userId !== null,
    gcTime: 0,
  })
}

export const useInstallationsCount = (status: InstallationStatus, searchPhrase: string): UseQueryResult<{ count: number }, Error> => {
  const authFetch = useAuthFetchWithResponse()

  return usePortalQueryFromPortal({
    queryKey: ['installationsCount', status, searchPhrase],
    queryFn: () => getInstallationsCount(status, searchPhrase, authFetch).then(res => res.value),
  })
}

export const useInstallation = (installationId: number | undefined, enabled = true): UseQueryResult<Nullable<InstallationDto>, Error> => {
  const authFetch = useAuthFetchWithResponse()

  return usePortalQueryFromPortal({
    queryKey: ['installation', installationId],
    queryFn: () => getInstallation(installationId!, authFetch).then(res => res.value),
    enabled: !isUndefined(installationId) && enabled,
  })
}

export const installationConfigKey = (installationId: number | undefined) => ['installation', installationId, 'config'] as const
export const useInstallationConfig = (installationId: number | undefined, enabled = true): UseQueryResult<Nullable<InstallationConfigDto>, Error> => {
  const authFetch = useAuthFetchWithResponse()

  return usePortalQueryFromPortal({
    queryKey: installationConfigKey(installationId),
    queryFn: () => getInstallationConfig(installationId!, authFetch).then(res => res.value),
    enabled: !isUndefined(installationId) && enabled,
  })
}

export const useEmcIdlePeriods = (installationId: number | undefined, enabled = true): UseQueryResult<Nullable<EmcIdlePeriodDto>[], Error> => {
  const authFetch = useAuthFetchWithResponse()

  return usePortalQueryFromPortal({
    queryKey: ['installation', installationId, 'emc-idle-periods'],
    queryFn: () => getEmcIdlePeriods(installationId!, authFetch).then(res => res.value),
    enabled: !isUndefined(installationId) && enabled,
  })
}

export const usePowerBuyPrices = (
  installationId: number | undefined,
  enabled = true
): UseQueryResult<Nullable<InstallationPowerPriceDto>[], Error> => {
  const authFetch = useAuthFetchWithResponse()

  return usePortalQueryFromPortal({
    queryKey: ['installation', installationId, 'power-buy-prices'],
    queryFn: () =>
      getPowerBuyPrices(installationId!, authFetch)
        .then(res => res.value)
        .then(data => (data ? transposeArrayType(data) : [])),
    enabled: !isUndefined(installationId) && enabled,
  })
}

export const usePowerSellPrices = (
  installationId: number | undefined,
  enabled = true
): UseQueryResult<Nullable<InstallationPowerPriceDto>[], Error> => {
  const authFetch = useAuthFetchWithResponse()

  return usePortalQueryFromPortal({
    queryKey: ['installation', installationId, 'power-sell-prices'],
    queryFn: () =>
      getPowerSellPrices(installationId!, authFetch)
        .then(res => res.value)
        .then(data => (data ? transposeArrayType(data) : [])),
    enabled: !isUndefined(installationId) && enabled,
  })
}

export const useDynamicTariff = (installationId: string | number | undefined, enabled = true): UseQueryResult<Nullable<DynamicTariffDto>, Error> => {
  const authFetch = useAuthFetchWithResponse()

  return usePortalQueryFromPortal({
    queryKey: ['installation', installationId, 'dynamic-tariff'],
    queryFn: () => getDynamicTariff(installationId!, authFetch).then(res => res.value),
    enabled: !isUndefined(installationId) && enabled,
  })
}

export const modbusTcpServerKey = (installationId: number | undefined) => ['installation', installationId, 'modbus-tcp-server'] as const
/** Returns explicit null for 204 responses with no data (meaning a modbus tcp server has nor been enabled yet). */
export const useModbusTcpServer = (installationId: number | undefined, enabled = true): UseQueryResult<Nullable<ModbusTCPServerDto>, Error> => {
  const authFetch = useAuthFetchWithResponse()

  return usePortalQueryFromPortal({
    queryKey: modbusTcpServerKey(installationId),
    queryFn: () => getModbusTcpServer(installationId!, authFetch).then(res => res.value),
    enabled: !isUndefined(installationId) && enabled,
  })
}

/** 5 minutes */
const DEVICE_REFETCH_INTERVAL = 1000 * 60 * 5
export const installationDevicesKey = (installationId: number | undefined) => ['installation', installationId, 'device-links'] as const
export const useInstallationDevices = (installationId: number | undefined, enabled = true): UseQueryResult<Nullable<InstallationDevices>, Error> => {
  const authFetch = useAuthFetchWithResponse()

  return usePortalQueryFromPortal({
    queryKey: installationDevicesKey(installationId),
    queryFn: () =>
      getDeviceLinks(installationId!, authFetch)
        .then(res => res.value)
        .then(data => (data ? transposeArrayType(data) : []))
        .then(devices => {
          // Keep only the devices that are still active (do not have a stop date)
          const activeDevices = devices.filter(device => !device.stopAt)
          return {
            emc: activeDevices.find(device => device.deviceType === 'emc') ?? null,
            storages: activeDevices.filter(device => device.deviceType === 'storage') ?? null,
            activeDevices,
            allDevices: devices,
          }
        }),
    enabled: !isUndefined(installationId) && enabled,
    refetchInterval: DEVICE_REFETCH_INTERVAL,
  })
}

const installationSubDevicesKey = (installationId: number | undefined, emcId: string | null) =>
  ['installation', installationId, 'device-links', emcId, 'sub-devices'] as const
export const useInstallationSubDevices = (
  installationId: number | undefined,
  emcId: string | null,
  enabled = true,
  retry?: boolean | undefined
): UseQueryResult<Nullable<SubDevicesDto>, Error> => {
  const authFetch = useAuthFetchWithResponse()

  return usePortalQueryFromPortal({
    queryKey: installationSubDevicesKey(installationId, emcId),
    queryFn: () => getSubDevices(installationId!, emcId!, authFetch).then(res => res.value),
    enabled: !isUndefined(installationId) && emcId !== null && enabled,
    refetchInterval: DEVICE_REFETCH_INTERVAL,
    retry,
  })
}

export const useInstallationSubDeviceQueries = (
  installationId: number | undefined,
  storageIds: (string | null)[],
  enabled = true,
  retry?: boolean | undefined
): UseQueryResult<SubDevicesDto | null, Error>[] => {
  const authFetch = useAuthFetchWithResponse()

  return usePortalQueries(
    storageIds.map(storageId => ({
      queryKey: installationSubDevicesKey(installationId, storageId),
      queryFn: () => getSubDevices(installationId!, storageId!, authFetch),
      enabled: !isUndefined(installationId) && storageId !== null && enabled,
      refetchInterval: DEVICE_REFETCH_INTERVAL,
      retry,
    }))
  )
}

export const useEmcConfig = (
  installationId: number | undefined,
  emcId: string | null,
  enabled = true
): UseQueryResult<Nullable<EmcConfigDto>, Error> => {
  const authFetch = useAuthFetchWithResponse()

  return usePortalQueryFromPortal({
    queryKey: ['installation', installationId, 'device-links', emcId, 'emc-config'],
    queryFn: () => getEmcConfig(installationId!, emcId!, authFetch).then(res => res.value),
    enabled: !isUndefined(installationId) && emcId !== null && enabled,
  })
}

export const getStorageConfigKey = (installationId: number | undefined, storageId: string | null) =>
  ['installation', installationId, 'device-links', storageId, 'storage-config'] as const

export const emcConfigKey = (installationId: number | undefined, emcId: string | null) =>
  ['installation', installationId, 'device-links', emcId, 'emc-config'] as const

export const useStorageConfig = (
  installationId: number | undefined,
  storageId: string | null,
  enabled = true
): UseQueryResult<Nullable<StorageConfigDto>, Error> => {
  const authFetch = useAuthFetchWithResponse()

  return usePortalQuery({
    queryKey: getStorageConfigKey(installationId, storageId),
    queryFn: () => getStorageConfig(installationId!, storageId!, authFetch),
    enabled: !isUndefined(installationId) && storageId !== null && enabled,
  })
}

export const useStorageConfigQueries = (
  installationId: number | undefined,
  storageIds: string[],
  enabled = true
): UseQueryResult<Nullable<StorageConfigDto>, Error>[] => {
  const authFetch = useAuthFetchWithResponse()

  return usePortalQueries(
    storageIds.map(storageId => ({
      queryKey: getStorageConfigKey(installationId, storageId),
      queryFn: () => getStorageConfig(installationId!, storageId!, authFetch),
      enabled: !isUndefined(installationId) && storageId !== null && enabled,
    }))
  )
}

export const getStorageBatteryOverviewKey = (installationId: number | undefined, storageHardwareDeviceId: string | null) =>
  ['installation', installationId, 'storage', storageHardwareDeviceId, 'battery-overview'] as const
export const useStorageBatteryOverview = (
  installationId: number | undefined,
  storageHardwareDeviceId: string | null,
  enabled = true
): UseQueryResult<Nullable<BatteryOverviewDto & { storageHardwareDeviceId?: string }>, Error> => {
  const authFetch = useAuthFetchWithResponse()

  return usePortalQuery({
    queryKey: getStorageBatteryOverviewKey(installationId, storageHardwareDeviceId),
    // TODO: handle error
    queryFn: () => getStorageBatteryOverview(storageHardwareDeviceId!, authFetch),
    enabled: !isUndefined(installationId) && !!storageHardwareDeviceId && enabled,
    retry: 1,
  })
}

export const useStorageBatteryOverviewQueries = (
  installationId: number | undefined,
  storageHardwareDeviceIds: string[],
  enabled = true
): UseQueryResult<Nullable<BatteryOverviewDto & { storageHardwareDeviceId?: string }>, Error>[] => {
  const authFetch = useAuthFetchWithResponse()

  return usePortalQueries(
    storageHardwareDeviceIds.map(storageHardwareDeviceId => ({
      queryKey: getStorageBatteryOverviewKey(installationId, storageHardwareDeviceId),
      queryFn: () => getStorageBatteryOverview(storageHardwareDeviceId!, authFetch),
      enabled: !isUndefined(installationId) && !!storageHardwareDeviceId && enabled,
      retry: 1,
    }))
  )
}

export const accountLinksKey = (installationId: number | undefined) => ['installation', installationId, 'account-links'] as const
export const useAccountLinks = (installationId: number | undefined, enabled = true): UseQueryResult<Nullable<AccountLinkDto>[], Error> => {
  const authFetch = useAuthFetchWithResponse()

  return usePortalQueryFromPortal({
    queryKey: accountLinksKey(installationId),
    queryFn: () => getAccountLinks(installationId!, authFetch).then(res => (res.value ? transposeArrayType(res.value) : [])),
    enabled: !isUndefined(installationId) && enabled,
  })
}

export const useWallboxesIdentityStatus = (
  emcHardwareDeviceId: string | null,
  enabled = true
): UseQueryResult<Nullable<WallboxIdentityStatusDto>, Error> => {
  const authFetch = useAuthFetchWithResponse()

  return usePortalQueryFromPortal({
    queryKey: ['emc', emcHardwareDeviceId, 'wallboxes-identity-status'],
    queryFn: () => getWallboxesIdentityStatus(emcHardwareDeviceId!, authFetch).then(res => res.value),
    enabled: !!emcHardwareDeviceId && enabled,
  })
}
